import {
  getAttributeValueTuples,
  getFirstAttributeValue,
  type Product,
} from '@scayle/storefront-nuxt'
import { computed, type MaybeRefOrGetter, toRef } from 'vue'
import { useRouteHelpers } from '~/composables'
import {
  formatColors,
  getDeepestCategoryAttribute,
  getPrimaryImage,
  getProductSiblings,
  sortProductImages,
} from '~/utils'
import { useI18n } from '#i18n'
import type { ProductSibling } from '~/types/siblings'
import {
  SHOE_SIZES_ATTRIBUTES_KEYS,
  type SizeKey,
  type SizeSystem,
} from '~/constants'

export function useProductBaseInfo(
  productItem: MaybeRefOrGetter<Product | undefined | null>,
) {
  const product = toRef(productItem)
  const { getProductDetailRoute } = useRouteHelpers()

  const { t } = useI18n()

  const brand = computed(() => {
    return (
      getFirstAttributeValue(product.value?.attributes, 'brand')?.label ?? ''
    )
  })

  const name = computed(() => {
    return (
      getFirstAttributeValue(product.value?.attributes, 'name')?.label ?? ''
    )
  })

  const description = computed(() => {
    return (
      getFirstAttributeValue(product.value?.attributes, 'description')?.label ??
      ''
    )
  })

  const price = computed(() => {
    return product.value ? product.value.priceRange?.min : undefined
  })

  const lowestPriorPrice = computed(() => {
    return product.value ? product.value.lowestPriorPrice : undefined
  })

  const color = computed(() => {
    return formatColors(
      getAttributeValueTuples(product.value?.attributes, 'color'),
    )
  })

  const images = computed(() => {
    if (!product.value) {
      return []
    }

    return sortProductImages(product.value.images)
  })

  const image = computed(() => {
    if (!product.value) {
      return
    }

    return getPrimaryImage(product.value.images)
  })

  const siblings = computed<ProductSibling[]>(() => {
    return getProductSiblings(product.value, 'color', { sortBySoldOut: true })
  })

  const nonSoldOutSiblings = computed<ProductSibling[]>(() => {
    return getProductSiblings(product.value, 'color', { omitSoldOut: true })
  })

  const link = computed(() => {
    return product.value
      ? getProductDetailRoute(
          product.value.id,
          name.value,
          color.value,
          brand.value,
        )
      : undefined
  })

  const productCode = computed(() => {
    return (
      getFirstAttributeValue(product.value?.attributes, 'productCode')?.label ??
      ''
    )
  })

  const alt = computed(() => {
    return t('product_image.alt', {
      productName: name.value,
      colors: color.value,
      brand: brand.value,
    })
  })

  const longestCategoryList = computed(() => {
    if (
      !product.value ||
      !product.value.categories ||
      product.value.categories.length === 0
    ) {
      return []
    }

    return product.value.categories.reduce(
      (longestArray, currentArray) =>
        currentArray.length > longestArray.length ? currentArray : longestArray,
      [],
    )
  })

  const variants = computed(() => {
    const list = product.value?.variants || []
    if (list.length === 0) {
      return []
    }
    // sort by sizeOrderNo attribute
    // use normalize to cover array and object cases
    return list.slice().sort((a, b) => {
      const normalize = (val: any) => {
        if (Array.isArray(val)) {
          return val[0]?.value ?? 0 // pick first if array
        }
        return val?.value ?? 0
      }

      const aVal = normalize(a.attributes?.sizeOrderNo?.values)
      const bVal = normalize(b.attributes?.sizeOrderNo?.values)

      return aVal - bVal
    })
  })

  const hasOneVariantOnly = computed(() => {
    return variants.value.length === 1
  })

  const availableSizeSystems = computed(() => {
    const present = new Set<SizeSystem>()

    for (const v of variants.value) {
      const attrs = v?.attributes ?? {}

      for (const [system, attrKey] of Object.entries(
        SHOE_SIZES_ATTRIBUTES_KEYS,
      ) as [SizeSystem, SizeKey][]) {
        if (attrKey in attrs && attrs[attrKey] != null) {
          present.add(system)
        }
      }
    }

    // keep stable order EU → US → UK
    return Object.keys(SHOE_SIZES_ATTRIBUTES_KEYS).filter(
      (system): system is SizeSystem => present.has(system as SizeSystem),
    )
  })

  const isShoeProduct = computed(() => {
    const attrs = variants.value.at(0)?.attributes ?? {}
    return ['sizeEu', 'sizeUk', 'sizeUs'].some((key) => key in attrs)
  })

  const category = computed(() =>
    product.value ? getDeepestCategoryAttribute(product.value) : null,
  )

  return {
    alt,
    brand,
    name,
    description,
    price,
    lowestPriorPrice,
    color,
    image,
    images,
    siblings,
    nonSoldOutSiblings,
    link,
    longestCategoryList,
    variants,
    hasOneVariantOnly,
    productCode,
    isShoeProduct,
    availableSizeSystems,
    category,
  }
}
